import React, { useState, useRef, useEffect } from "react";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import "../../index.css";

const FloatingChatbot = ({ API_EndPoint, userInfo }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [messages, setMessages] = useState([
    { text: "Hi! How can I help you today?", sender: "bot" },
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [newId, setNewId] = useState(null);

  const [position, setPosition] = useState({ bottom: 24, right: 24 });
  const buttonRef = useRef(null);
  const isDragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const messagesEndRef = useRef(null);

  // --- Get session ID ---
  async function getSessionId() {
    try {
      const response = await fetch(`${API_EndPoint}/new_session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: userInfo?.userid,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      setNewId(data?.session_id);
      return data?.session_id;
    } catch (error) {
      console.error("Failed to fetch session:", error);
      return null;
    }
  }

  useEffect(() => {
    getSessionId();
  }, [refresh]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // --- Handle message send ---
  const handleSend = async (customInput) => {
    const query = customInput || inputValue.trim();
    if (query === "") return;

    setIsSearching(true);
    const userMessage = { text: query, sender: "user" };
    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");

    try {
      const response = await fetch(`${API_EndPoint}/query`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question: query,
          session_id: newId,
        }),
      });

      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);

      const data = await response.json();
      const { summary, options, chart_img, routes, card } = data;

      if (options?.length > 0) {
        setMessages((prev) => [
          ...prev,
          { text: summary, sender: "bot" },
          { options, sender: "bot" },
        ]);
      } else if (routes?.length > 0) {
        setMessages((prev) => [
          ...prev,
          { text: summary, sender: "bot" },
          { routes, sender: "bot" },
        ]);
      } else if (summary) {
        setMessages((prev) => [...prev, { text: summary, sender: "bot" }]);
      } else if (chart_img) {
        setMessages((prev) => [...prev, { image: chart_img, sender: "bot" }]);
      } else if (card?.length > 0) {
        setMessages((prev) => [...prev, { card: card, sender: "bot" }]);
      }
    } catch (error) {
      console.error("POST error:", error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleRefresh = () => {
    setRefresh((prev) => prev + 1);
    setMessages([{ text: "Hi! How can I help you today?", sender: "bot" }]);
  };

  // --- Draggable button logic ---
  const handleMouseDown = (e) => {
    isDragging.current = true;
    const rect = buttonRef.current.getBoundingClientRect();
    dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current) return;
    const newLeft = e.clientX - dragOffset.current.x;
    const newTop = e.clientY - dragOffset.current.y;
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const boundedLeft = Math.min(
      Math.max(newLeft, 0),
      windowWidth - buttonRect.width
    );
    const boundedTop = Math.min(
      Math.max(newTop, 0),
      windowHeight - buttonRect.height
    );
    setPosition({
      bottom: windowHeight - boundedTop - buttonRect.height,
      right: windowWidth - boundedLeft - buttonRect.width,
    });
  };

  const handleMouseUp = () => {
    isDragging.current = false;
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  return (
    <div
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      {isOpen && (
        <div
          style={{
            position: "fixed",
            bottom: position.bottom + 76,
            right: position.right,
            width: "380px",
            height: "550px",
            backgroundColor: "#fff",
            borderRadius: "16px",
            border: "1px solid #6a70d6",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            zIndex: 1000,
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
          className={isSearching ? "neonBlink" : ""}
        >
          {/* Header */}
          <div
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              padding: "20px",
              color: "#fff",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "20px",
                }}
              >
                🤖
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: "16px" }}>F E M I</div>
                <div style={{ fontSize: "12px", opacity: 0.9 }}>
                  {isSearching ? "Thinking..." : "Online"}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <button
                onClick={handleRefresh}
                style={{
                  background: "none",
                  border: "none",
                  color: "#fff",
                  cursor: "pointer",
                  fontSize: "24px",
                }}
              >
                <RefreshIcon />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#fff",
                  cursor: "pointer",
                  fontSize: "24px",
                }}
              >
                <CloseIcon />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "20px",
              backgroundColor: "#f5f5f5",
            }}
          >
            {messages.map((message, index) => {
              const isUser = message.sender === "user";
              return (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    justifyContent: isUser ? "flex-end" : "flex-start",
                    marginBottom: "12px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: isUser ? "flex-end" : "flex-start",
                      maxWidth: "80%",
                    }}
                  >
                    {/* Text message */}
                    {message?.text && (
                      <div
                        style={{
                          backgroundColor: isUser ? "#667eea" : "#fff",
                          color: isUser ? "#fff" : "#333",
                          padding: "10px 14px",
                          borderRadius: isUser
                            ? "18px 18px 0 18px"
                            : "18px 18px 18px 0",
                          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                          fontSize: "14px",
                          lineHeight: "1.5",
                          wordWrap: "break-word",
                        }}
                      >
                        {message.text}
                      </div>
                    )}

                    {/* Options */}
                    {message?.options && (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                          marginTop: "10px",
                        }}
                      >
                        {message.options.map((opt, i) => (
                          <button
                            key={i}
                            onClick={() => handleSend(opt)}
                            style={{
                              background:
                                "linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)",
                              border: "1px solid #ddd",
                              borderRadius: "12px",
                              padding: "8px 12px",
                              textAlign: "left",
                              cursor: "pointer",
                              fontSize: "14px",
                              transition: "background 0.2s",
                            }}
                            onMouseOver={(e) =>
                              (e.currentTarget.style.background =
                                "linear-gradient(135deg, #667eea20, #764ba220)")
                            }
                            onMouseOut={(e) =>
                              (e.currentTarget.style.background =
                                "linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)")
                            }
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                    )}
                    {/* Routes */}
                    {message?.routes && (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                          marginTop: "10px",
                        }}
                      >
                        {message.options.map((opt, i) => (
                          <button
                            key={i}
                            onClick={() => handleSend(opt)}
                            style={{
                              background:
                                "linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)",
                              border: "1px solid #ddd",
                              borderRadius: "12px",
                              padding: "8px 12px",
                              textAlign: "left",
                              cursor: "pointer",
                              fontSize: "14px",
                              transition: "background 0.2s",
                            }}
                            onMouseOver={(e) =>
                              (e.currentTarget.style.background =
                                "linear-gradient(135deg, #667eea20, #764ba220)")
                            }
                            onMouseOut={(e) =>
                              (e.currentTarget.style.background =
                                "linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)")
                            }
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Image */}
                    {message?.image && (
                      <img
                        src={message.image}
                        alt={`img-${index}`}
                        style={{
                          marginTop: "10px",
                          maxWidth: "100%",
                          borderRadius: "12px",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                        }}
                      />
                    )}

                    {message?.card && <CardItem data={message.card} />}
                  </div>
                </div>
              );
            })}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div
            style={{
              padding: "16px",
              backgroundColor: "#fff",
              borderTop: "1px solid #e0e0e0",
            }}
          >
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type a message..."
                style={{
                  flex: 1,
                  padding: "12px 16px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "24px",
                  fontSize: "14px",
                  outline: "none",
                }}
              />
              <button
                onClick={() => handleSend()}
                style={{
                  width: "44px",
                  height: "44px",
                  borderRadius: "50%",
                  border: "none",
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "#fff",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "20px",
                  boxShadow: "0 2px 8px rgba(102,126,234,0.4)",
                }}
              >
                <AutoFixHighIcon />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        onMouseDown={handleMouseDown}
        style={{
          position: "fixed",
          bottom: position.bottom,
          right: position.right,
          width: "65px",
          height: "65px",
          borderRadius: "50%",
          border: "none",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "#fff",
          fontSize: "28px",
          cursor: "grab",
          boxShadow: "0 4px 16px rgba(102,126,234,0.4)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1001,
        }}
      >
        {isOpen ? "×" : "🤖"}
      </button>
    </div>
  );
};

export default FloatingChatbot;

const CardItem = ({ data }) => {
  return (
    <div
      className="Chatbot-Card"
      style={{ background: "#fff", padding: "10px", borderRadius: "5px" }}
    >
      {data?.map((item, index) => (
        <div key={index} className="Chatbot-Card-Item">
          {Object.entries(item).map(([key, value]) => (
            <p key={key} style={{ fontSize: "12px", margin: "2px" }}>
              <strong>{key}:</strong> {value}
            </p>
          ))}
        </div>
      ))}
    </div>
  );
};
